import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  View as RNView,
  Modal as RNModal,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import DropDownPicker from "react-native-dropdown-picker";
import { useSelector, useDispatch } from "react-redux";
import reportsActions from "../../../redux/actions/reportsActions";
import { useColumnResize } from "../hooks/useColumnResize";
import { scorecardPeriodModel, usageL36M2V2Model } from "../../../models/reportsModel";
import { getUsageL36M2V2, getUsageL36M2V2Period } from "../../../services/inventoryService";
import AsyncStorage from '@react-native-async-storage/async-storage';
import DateRangeFilter, { DateRange } from "../components/DateRangeFilter";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import { useTranslation } from "../../../utils/useTranslation";
import { scoreCardTranslationMap } from "../../../utils/translations";
import { useAppTheme } from "../../../hooks/useAppTheme";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const COL_ITEM_W = 220;
const COL_CUSTOMER_W = 180;
const COL_SLP_W = 130;
const COL_YEAR_W = 60;
const COL_TOTAL_W = 70;
const FIXED_COLS_W = COL_ITEM_W + COL_CUSTOMER_W + COL_SLP_W + COL_YEAR_W;
const MIN_MONTH_W = 52;

const monthKey = (year: number, monthIdx: number) => `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

const monthInRange = (year: number, monthIdx: number, range: DateRange | null) =>
  !range || (monthKey(year, monthIdx) >= range.start && monthKey(year, monthIdx) <= range.end);

const getRowTotal = (row: usageL36M2V2Model, range: DateRange | null) =>
  MONTHS.reduce((sum, m, i) => sum + (monthInRange(row.Year, i, range) ? ((row[m as keyof usageL36M2V2Model] as number) || 0) : 0), 0);

// ── Day-precision helpers (Daily/Weekly views) ────────────────────────────────
const MAX_PERIOD_DAYS = 92; // must match the SP's Daily/Weekly range cap

const toDayStart = (s: string) => (s.length === 7 ? `${s}-01` : s);
const toDayEnd = (s: string) => {
  if (s.length !== 7) return s;
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(5, 7), 10);
  return `${s}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};
const addDaysISO = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const daysBetween = (a: string, b: string) =>
  Math.round((new Date(`${b}T00:00:00`).getTime() - new Date(`${a}T00:00:00`).getTime()) / 86400000);
const enumerateDays = (from: string, to: string) => {
  const out: string[] = [];
  let d = from;
  while (d <= to && out.length <= MAX_PERIOD_DAYS + 1) {
    out.push(d);
    d = addDaysISO(d, 1);
  }
  return out;
};

function toItems(values: string[]) {
  return values.sort().map((v) => ({ label: v, value: v }));
}

// ── Filter dropdown wrapper ───────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  items: { label: string; value: string }[];
  open?: boolean;
  setOpen?: (o: boolean) => void;
  zIndex?: number;
  zIndexInverse?: number;
  onOpen?: () => void;
  isDark?: boolean;
  colors?: any;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label, value, onChange, items,
  open, setOpen, zIndex, zIndexInverse, onOpen,
  isDark, colors
}) => {
  const filterItemStyle = { minWidth: 160, flex: 1 } as const;
  const filterLabelStyle = { fontSize: 12, color: isDark ? "#aaa" : "#555", marginBottom: 4, fontWeight: "500" as const };

  return (
    <View style={[filterItemStyle, { zIndex }]}>
      {Platform.OS === "web" && (
        <style type="text/css">{`
          [data-hoveritem="true"]:hover, [data-hover-item="true"]:hover {
            background-color: ${isDark ? "#333" : "#f5f5f5"} !important;
          }
        `}</style>
      )}
      <Text style={filterLabelStyle}>{label}</Text>
      <DropDownPicker
        multiple={true}
        mode="BADGE"
        open={open!}
        setOpen={setOpen as any}
        items={items}
        value={value}
        setValue={onChange as any}
        placeholder="All"
        listMode="SCROLLVIEW"
        maxHeight={600}
        style={{ borderColor: isDark ? "#444" : "#ddd", borderRadius: 6, minHeight: 38, backgroundColor: colors?.surface || "#fff" }}
        dropDownContainerStyle={{ borderColor: isDark ? "#444" : "#ddd", borderRadius: 6, backgroundColor: colors?.surface || "#fff" }}
        textStyle={{ color: colors?.text || "#000" }}
        badgeTextStyle={{ color: colors?.text || "#000" }}
        badgeColors={isDark ? ["#334155"] : ["#E2E8F0"]}
        zIndex={zIndex}
        zIndexInverse={zIndexInverse}
        onOpen={onOpen}
        badgeDotColors={["#1976d2"]}
        searchable={true}
        searchPlaceholder="Search Bar"
        searchContainerStyle={{ borderBottomColor: isDark ? "#444" : "#f0f0f0", borderBottomWidth: 1, padding: 10 }}
        searchTextInputStyle={{ borderColor: isDark ? "#444" : "#ddd", color: colors?.text || "#000", borderWidth: 1, borderRadius: 6, height: 36, paddingHorizontal: 12, fontSize: 13 }}
        itemProps={{ dataSet: { hoveritem: "true" } } as any}
        dropDownDirection="BOTTOM"
        theme={isDark ? "DARK" : "LIGHT"}
      />
    </View>
  );
};

function getCacheWindowStart(): string {
  const now = new Date();
  const start = new Date(now);
  start.setHours(now.getHours() < 12 ? 0 : 12, 0, 0, 0);
  return start.toLocaleString("en-AU", { hour12: true });
}

const rszStyle = { position: "absolute" as const, right: 0, top: 0, bottom: 0, width: 6, cursor: "col-resize", zIndex: 2 };

// ── Main screen ───────────────────────────────────────────────────────────────
export default function UsageSalesScorecardScreen() {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { usageL36M2V2Report, isUsageL36M2V2ReportListLoaded, usageL36M2V2HiddenColumns } = useSelector(
    (state: any) => state.reports
  );
  const rows: usageL36M2V2Model[] = usageL36M2V2Report;
  const hiddenCols = usageL36M2V2HiddenColumns || [];
  const searchScrollRef = useRef<any>(null);

  useEffect(() => {
    getUsageL36M2V2();
  }, []);

  // ── Horizontal scroll sync ────────────────────────────────────────────────
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const onBodyHScroll = useRef(
    Animated.event(
      [{ nativeEvent: { contentOffset: { x: scrollAnim } } }],
      { useNativeDriver: false }
    )
  ).current;

  // ── Data area height ──────────────────────────────────────────────────────
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [dataAreaHeight, setDataAreaHeight] = useState(() => Math.max(200, windowHeight - 380));
  const onDataAreaLayout = useCallback((e: any) => {
    setDataAreaHeight(e.nativeEvent.layout.height);
  }, []);

  // ── Granularity (Monthly / Weekly / Daily) ────────────────────────────────
  const [granularity, setGranularity] = useState<"M" | "W" | "D">("M");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [periodRows, setPeriodRows] = useState<scorecardPeriodModel[]>([]);
  const [periodLoading, setPeriodLoading] = useState(false);

  useEffect(() => {
    if (granularity === "M" || !dateRange) return;
    let cancelled = false;
    setPeriodLoading(true);
    getUsageL36M2V2Period(granularity, dateRange.start, dateRange.end).then((data) => {
      if (!cancelled) {
        setPeriodRows(data);
        setPeriodLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [granularity, dateRange]);

  // Dynamic period columns: months (M), calendar days (D), or weeks present in data (W)
  const periodKeys = useMemo(() => {
    if (granularity === "M" || !dateRange) return MONTHS;
    if (granularity === "D") return enumerateDays(dateRange.start, dateRange.end);
    const seen = new Set<string>();
    for (const r of periodRows) seen.add(`${r.Year}-W${String(r.Week).padStart(2, "0")}`);
    return Array.from(seen).sort();
  }, [granularity, dateRange, periodRows]);

  const periodLabel = (k: string) => {
    if (granularity === "D") return `${k.slice(8, 10)}/${k.slice(5, 7)}`;
    if (granularity === "W") return k.slice(5);
    return t(k.toLowerCase());
  };

  // Pivot fetched daily/weekly rows into one row per dimension combo with period-keyed values
  const pivotedPeriodRows = useMemo(() => {
    if (granularity === "M") return [];
    const map = new Map<string, Record<string, any>>();
    for (const r of periodRows) {
      // Year excluded from the key: period keys (dates/week-years) already carry their own year,
      // so this keeps one row per item/customer/slp even when the range crosses a year boundary.
      const dimKey = [r.ItemName, r.Customer, r.SlpName, r["Dispatch State"], r["Delivery State"], r["Item Group"], r["Blank/Customer"]].join("|");
      let row = map.get(dimKey);
      if (!row) {
        row = {
          Year: r.Year,
          "Dispatch State": r["Dispatch State"],
          "Delivery State": r["Delivery State"],
          "Blank/Customer": r["Blank/Customer"],
          Customer: r.Customer,
          SlpName: r.SlpName,
          "Item Group": r["Item Group"],
          ItemName: r.ItemName,
        };
        map.set(dimKey, row);
      }
      const pk = granularity === "D" ? String(r.Date).slice(0, 10) : `${r.Year}-W${String(r.Week).padStart(2, "0")}`;
      row[pk] = (row[pk] || 0) + (r.Qty || 0);
    }
    return Array.from(map.values()) as unknown as usageL36M2V2Model[];
  }, [granularity, periodRows]);

  const defaultColWidths = useMemo(() => {
    const visibleFixedW = (hiddenCols.includes("ItemName") ? 0 : COL_ITEM_W) +
      (hiddenCols.includes("Customer") ? 0 : COL_CUSTOMER_W) +
      (hiddenCols.includes("SlpName") ? 0 : COL_SLP_W) +
      (hiddenCols.includes("Year") ? 0 : COL_YEAR_W);
    const visibleTotalW = hiddenCols.includes("Total") ? 0 : COL_TOTAL_W;
    const visiblePeriods = periodKeys.filter(k => !hiddenCols.includes(k));
    const mw = visiblePeriods.length > 0
      ? Math.max(MIN_MONTH_W, Math.floor((windowWidth - 48 - visibleFixedW - visibleTotalW) / visiblePeriods.length))
      : MIN_MONTH_W;

    const obj: Record<string, number> = { ItemName: COL_ITEM_W, Customer: COL_CUSTOMER_W, SlpName: COL_SLP_W, Year: COL_YEAR_W, Total: COL_TOTAL_W };
    for (const k of periodKeys) obj[k] = mw;
    return obj;
  }, [windowWidth, hiddenCols, periodKeys]);

  const [colWidths, setColWidths] = useState<Record<string, number>>(defaultColWidths);
  useEffect(() => {
    setColWidths((prev) => {
      const obj: Record<string, number> = { ...prev };
      for (const k of periodKeys) obj[k] = defaultColWidths[k];
      return obj;
    });
  }, [defaultColWidths]);
  const startResize = useColumnResize(setColWidths);
  const tableWidth = ["ItemName", "Customer", "SlpName", "Year", ...periodKeys, "Total"]
    .reduce((s, k) => hiddenCols.includes(k) ? s : s + (colWidths[k] ?? 0), 0);

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyCell = useCallback((key: string, text: string) => {
    if (Platform.OS === "web" && typeof navigator !== "undefined") {
      try { navigator.clipboard?.writeText(text); } catch { }
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
  }, []);

  const CellWrapper: any = Platform.OS === "web" ? View : TouchableOpacity;

  // ── Filter open state (native only) ──────────────────────────────────────
  const [yearOpen, setYearOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [slpOpen, setSlpOpen] = useState(false);
  const [itemGroupOpen, setItemGroupOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  const allColOptions = useMemo(() => [
    { label: t(scoreCardTranslationMap["Item Name"]), value: "ItemName" },
    { label: t(scoreCardTranslationMap["Customer"]), value: "Customer" },
    { label: t(scoreCardTranslationMap["Sales Person"]), value: "SlpName" },
    { label: t(scoreCardTranslationMap["Year"]), value: "Year" },
    ...periodKeys.map(k => ({ label: periodLabel(k), value: k })),
    { label: t(scoreCardTranslationMap["Total"]), value: "Total" }
  ], [periodKeys, granularity]);

  const columnOrder = useSelector((state: any) => state.reports.usageL36M2V2ColumnOrder) || [];

  const getOrderedKeys = useCallback((allKeys: string[]) => {
    // Saved column order only applies to the Monthly layout (its keys are month names)
    if (granularity !== "M" || columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k: string) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k: string) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  }, [columnOrder, granularity]);

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    if (granularity !== "M") return;
    const allKeys = allColOptions.map((c) => c.value);
    const currentKeys = getOrderedKeys(allKeys).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    dispatch(reportsActions.setUsageL36M2V2ColumnOrder(newKeys));
    AsyncStorage.setItem("usage_scorecard_column_order", JSON.stringify(newKeys));
  }, [columnOrder, allColOptions, getOrderedKeys, dispatch, granularity]);

  const resetColumnOrder = () => {
    dispatch(reportsActions.setUsageL36M2V2ColumnOrder([]));
    dispatch(reportsActions.setUsageL36M2V2HiddenColumns([]));
    AsyncStorage.removeItem("usage_scorecard_column_order");

    setColWidths(defaultColWidths);
  };

  useEffect(() => {
    if (columnOrder.length === 0) {
      AsyncStorage.getItem("usage_scorecard_column_order").then((val) => {
        if (val) {
          try {
            const parsed = JSON.parse(val);
            if (parsed && parsed.length > 0) {
              dispatch(reportsActions.setUsageL36M2V2ColumnOrder(parsed));
            }
          } catch { }
        }
      });
    }
  }, []);

  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const toggleColumn = (columnKey: string) => {
    const updated = hiddenCols.includes(columnKey)
      ? hiddenCols.filter((k: string) => k !== columnKey)
      : [...hiddenCols, columnKey];
    dispatch(reportsActions.setUsageL36M2V2HiddenColumns(updated));
  };

  const availableYears = useMemo(
    () => Array.from(new Set(rows.map((r) => String(r.Year)).filter(Boolean))).sort().reverse(),
    [rows]
  );

  // ── Filter values (empty array = no filter / show all) ───────────────────
  const [yearVal, setYearVal] = useState<string[]>([]);
  const [dispatchVal, setDispatchVal] = useState<string[]>([]);
  const [deliveryVal, setDeliveryVal] = useState<string[]>([]);
  const [slpVal, setSlpVal] = useState<string[]>([]);
  const [itemGroupVal, setItemGroupVal] = useState<string[]>([]);
  const [customerVal, setCustomerVal] = useState<string[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");

  const yearItems = useMemo(() => toItems(availableYears), [availableYears]);
  const dispatchItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Dispatch State"]).filter(Boolean)))), [rows]);
  const deliveryItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Delivery State"]).filter(Boolean)))), [rows]);
  const slpItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.SlpName).filter(Boolean)))), [rows]);
  const itemGroupItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Item Group"]).filter(Boolean)))), [rows]);
  const customerItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.Customer).filter(Boolean)))), [rows]);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const onSortPress = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setCurrentPage(1);
    setExpandedRow(null);
  };

  const sortIndicator = (col: string) =>
    sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const onSearchChange = (text: string) => {
    if (text.includes(",")) {
      const before = text.slice(0, text.indexOf(",")).trim();
      if (before) setSearchTerms((prev) => [...prev, before]);
      setSearchInput("");
    } else {
      setSearchInput(text);
    }
  };

  const filtered = useMemo(() => {
    const terms = [...searchTerms, searchInput.trim()].map((s) => s.toLowerCase()).filter(Boolean);
    const sourceRows = granularity === "M" ? rows : pivotedPeriodRows;
    return sourceRows.filter((r) => {
      if (yearVal.length > 0 && !yearVal.includes(String(r.Year))) return false;
      if (dispatchVal.length > 0 && !dispatchVal.includes(r["Dispatch State"])) return false;
      if (deliveryVal.length > 0 && !deliveryVal.includes(r["Delivery State"])) return false;
      if (slpVal.length > 0 && !slpVal.includes(r.SlpName)) return false;
      if (itemGroupVal.length > 0 && !itemGroupVal.includes(r["Item Group"])) return false;
      if (customerVal.length > 0 && !customerVal.includes(r.Customer)) return false;
      if (granularity === "M" && dateRange && (monthKey(r.Year, 11) < dateRange.start || monthKey(r.Year, 0) > dateRange.end)) return false;
      if (terms.length > 0) {
        const text = `${r.ItemName ?? ""} ${r.Customer ?? ""} ${r.SlpName ?? ""}`.toLowerCase();
        if (!terms.every((t) => text.includes(t))) return false;
      }
      return true;
    });
  }, [rows, pivotedPeriodRows, granularity, yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, dateRange, searchTerms, searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, dateRange, searchTerms, searchInput]);

  const rowTotal = (row: usageL36M2V2Model) =>
    granularity === "M"
      ? getRowTotal(row, dateRange)
      : periodKeys.reduce((s, k) => s + (((row as any)[k] as number) || 0), 0);

  const colTotals = useMemo(() => {
    const t: Record<string, number> = {};
    periodKeys.forEach((k, i) => {
      t[k] = filtered.reduce((sum, r) => sum + (granularity === "M"
        ? (monthInRange(r.Year, i, dateRange) ? ((r[k as keyof usageL36M2V2Model] as number) || 0) : 0)
        : (((r as any)[k] as number) || 0)), 0);
    });
    t["Total"] = periodKeys.reduce((sum, k) => sum + (t[k] || 0), 0);
    return t;
  }, [filtered, dateRange, periodKeys, granularity]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const valOf = (r: usageL36M2V2Model) => {
      if (sortCol === "Total") return rowTotal(r);
      if (periodKeys.includes(sortCol)) return granularity === "M"
        ? (monthInRange(r.Year, MONTHS.indexOf(sortCol), dateRange) ? ((r[sortCol as keyof usageL36M2V2Model] as number) || 0) : 0)
        : (((r as any)[sortCol] as number) || 0);
      return r[sortCol as keyof usageL36M2V2Model];
    };
    return [...filtered].sort((a, b) => {
      const aVal = valOf(a);
      const bVal = valOf(b);
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();
      return sortDir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filtered, sortCol, sortDir, dateRange, periodKeys, granularity]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goToPage = (p: number) => { setCurrentPage(Math.max(1, Math.min(p, totalPages))); setExpandedRow(null); };
  const onPageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); setExpandedRow(null); };

  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const toggleRow = (key: number) => setExpandedRow((prev) => (prev === key ? null : key));

  const colItem = [styles.colBase, { width: colWidths.ItemName }, hiddenCols.includes("ItemName") && { display: "none" as const }];
  const colCustomer = [styles.colBase, { width: colWidths.Customer }, hiddenCols.includes("Customer") && { display: "none" as const }];
  const colSlp = [styles.colBase, { width: colWidths.SlpName }, hiddenCols.includes("SlpName") && { display: "none" as const }];
  const colYear = [styles.colYearBase, { width: colWidths.Year ?? COL_YEAR_W }, hiddenCols.includes("Year") && { display: "none" as const }];

  // Auto-show Year column when date range spans multiple calendar years
  useEffect(() => {
    if (dateRange) {
      const startYear = parseInt(dateRange.start.slice(0, 4), 10);
      const endYear = parseInt(dateRange.end.slice(0, 4), 10);
      if (startYear !== endYear && hiddenCols.includes("Year")) {
        dispatch(reportsActions.setUsageL36M2V2HiddenColumns(hiddenCols.filter((k: string) => k !== "Year")));
      }
    }
  }, [dateRange]);

  if (!isUsageL36M2V2ReportListLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#1976d2" />
        <Text style={styles.loadingText}>Loading Usage Sales Scorecard...</Text>
      </View>
    );
  }

  const anyOpen = yearOpen || dispatchOpen || deliveryOpen || slpOpen || itemGroupOpen || customerOpen;
  const closeAllDropdowns = () => {
    setYearOpen(false);
    setDispatchOpen(false);
    setDeliveryOpen(false);
    setSlpOpen(false);
    setItemGroupOpen(false);
    setCustomerOpen(false);
    setColumnsOpen(false);
  };

  const resetFilters = () => {
    setYearVal([]);
    setDispatchVal([]);
    setDeliveryVal([]);
    setSlpVal([]);
    setItemGroupVal([]);
    setCustomerVal([]);
    setDateRange(null);
    setSearchTerms([]);
    setSearchInput("");
    setGranularity("M");
    setPeriodRows([]);
  };

  const onGranularityChange = (g: "M" | "W" | "D") => {
    if (g === granularity) return;
    setSortCol(null);
    setCurrentPage(1);
    if (g === "M") {
      setGranularity("M");
      if (dateRange) setDateRange({ start: dateRange.start.slice(0, 7), end: dateRange.end.slice(0, 7) });
    } else {
      let start: string, end: string;
      if (dateRange) {
        start = toDayStart(dateRange.start);
        end = toDayEnd(dateRange.end);
      } else {
        const now = new Date();
        end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        start = `${end.slice(0, 8)}01`;
      }
      if (daysBetween(start, end) > MAX_PERIOD_DAYS) end = addDaysISO(start, MAX_PERIOD_DAYS);
      setDateRange({ start, end });
      setGranularity(g);
    }
  };

  const onDateRangeChange = (r: DateRange | null) => {
    if (!r) {
      // Clearing the range in Daily/Weekly mode returns to Monthly (D/W require a range)
      if (granularity !== "M") {
        setGranularity("M");
        setPeriodRows([]);
      }
      setDateRange(null);
      return;
    }
    if (granularity !== "M" && daysBetween(r.start, r.end) > MAX_PERIOD_DAYS) {
      setDateRange({ start: r.start, end: addDaysISO(r.start, MAX_PERIOD_DAYS) });
      return;
    }
    setDateRange(r);
  };

  const exportToExcel = () => {
    const keys = getOrderedKeys(allColOptions.map((c) => c.value)).filter((k) => !hiddenCols.includes(k));
    const data = sorted.map((row) => {
      const obj: Record<string, string | number> = {};
      keys.forEach((k) => {
        const label = allColOptions.find((c) => c.value === k)?.label ?? k;
        if (k === "ItemName") obj[label] = row.ItemName;
        else if (k === "Customer") obj[label] = row.Customer;
        else if (k === "SlpName") obj[label] = row.SlpName;
        else if (k === "Year") obj[label] = row.Year;
        else if (k === "Total") obj[label] = rowTotal(row);
        else obj[label] = granularity === "M"
          ? (monthInRange(row.Year, MONTHS.indexOf(k), dateRange) ? ((row[k as keyof usageL36M2V2Model] as number) || 0) : "")
          : (((row as any)[k] as number) || 0);
      });
      return obj;
    });
    const granTag = granularity === "D" ? "_Daily" : granularity === "W" ? "_Weekly" : "";
    const dateSuffix = dateRange ? `_${dateRange.start}_to_${dateRange.end}` : "";
    exportDataToExcel(data, `Usage_Sales_Scorecard${granTag}_${new Date().toISOString().slice(0, 10)}${dateSuffix}`, "Usage Sales Scorecard");
  };

  return (
    <View style={styles.container}>
      {anyOpen && (
        <Pressable
          style={{ position: Platform.OS === "web" ? ("fixed" as any) : "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, height: Platform.OS === "web" ? ("100vh" as any) : 2000 }}
          onPress={closeAllDropdowns}
        />
      )}
      {/* ── Frozen header: title + search + filters ────────────────────────── */}
      <View style={styles.frozenHeader}>
        <View style={styles.filterPanel}>
          <View style={styles.filterHeaderRow}>
            <Text style={styles.filterHeading}>{t(scoreCardTranslationMap["FILTERS"])}</Text>
            <TouchableOpacity onPress={resetFilters} style={styles.resetBtn}>
              <Text style={styles.resetBtnText}>{t("resetFilter")}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.filterGrid}>
            <FilterDropdown
              label={t(scoreCardTranslationMap["Year"])}
              value={yearVal} onChange={setYearVal} items={yearItems}
              open={yearOpen} setOpen={setYearOpen} zIndex={700} zIndexInverse={100}
              onOpen={() => { setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
            <DateRangeFilter
              label={t("dateRange")}
              value={dateRange}
              onChange={onDateRangeChange}
              onOpen={closeAllDropdowns}
              precision={granularity === "M" ? "month" : "day"}
            />
            <FilterDropdown
              label={t(scoreCardTranslationMap["Sales Person"])}
              value={slpVal} onChange={setSlpVal} items={slpItems}
              open={slpOpen} setOpen={setSlpOpen} zIndex={600} zIndexInverse={200}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setItemGroupOpen(false); setCustomerOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
            <FilterDropdown
              label={t(scoreCardTranslationMap["Dispatch State"])}
              value={dispatchVal} onChange={setDispatchVal} items={dispatchItems}
              open={dispatchOpen} setOpen={setDispatchOpen} zIndex={500} zIndexInverse={300}
              onOpen={() => { setYearOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
            <FilterDropdown
              label={t(scoreCardTranslationMap["Delivery State"])}
              value={deliveryVal} onChange={setDeliveryVal} items={deliveryItems}
              open={deliveryOpen} setOpen={setDeliveryOpen} zIndex={400} zIndexInverse={400}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
            <FilterDropdown
              label={t(scoreCardTranslationMap["Item Group"])}
              value={itemGroupVal} onChange={setItemGroupVal} items={itemGroupItems}
              open={itemGroupOpen} setOpen={setItemGroupOpen} zIndex={300} zIndexInverse={500}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setCustomerOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
            <FilterDropdown
              label={t(scoreCardTranslationMap["Customer"])}
              value={customerVal} onChange={setCustomerVal} items={customerItems}
              open={customerOpen} setOpen={setCustomerOpen} zIndex={200} zIndexInverse={600}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setColumnsOpen(false); }}
              isDark={isDark} colors={colors}
            />
          </View>
        </View>
      </View>

      {/* ── Table section ──────────────────────────────────────────────────── */}
      <View style={styles.tableSection}>
        <View style={styles.tableCard}>

          {/* Top bar */}
          <View style={styles.tableTopBar}>
            <View style={{ flexDirection: "row", alignItems: "baseline" }}>
              <Text style={styles.tableTitle}>
                {granularity === "D" ? t("daily") : granularity === "W" ? t("weekly") : t("monthly")} {t("breakdown")} — {filtered.length.toLocaleString()} {t("rows")}
              </Text>
              <Text style={[styles.fetchedAt, { marginBottom: 0, marginLeft: 12 }]}>
                {t("dataUpTo")} {getCacheWindowStart()}
              </Text>
            </View>
            <View style={styles.pageSizeRow}>
              <View style={styles.granularityToggle}>
                {(["M", "W", "D"] as const).map((g) => (
                  <TouchableOpacity
                    key={g}
                    onPress={() => onGranularityChange(g)}
                    style={[styles.granBtn, granularity === g && styles.granBtnActive]}
                  >
                    <Text style={[styles.granTxt, granularity === g && styles.granTxtActive]}>
                      {g === "M" ? t("monthly") : g === "W" ? t("weekly") : t("daily")}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              {(sortCol !== null || JSON.stringify(colWidths) !== JSON.stringify(defaultColWidths)) && (
                <TouchableOpacity
                  onPress={() => {
                    setSortCol(null);
                    setColWidths(defaultColWidths);
                  }}
                  style={styles.resetSortBtn}
                >
                  <Text style={styles.resetSortTxt}>{t("reset")}</Text>
                </TouchableOpacity>
              )}
              <RNView style={{ marginRight: 12 }}>
                <TouchableOpacity
                  ref={settingsBtnRef}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    backgroundColor: columnsOpen ? (isDark ? "#2a3b4c" : "#E0E7FF") : (isDark ? "#333" : "#F3F4F6"),
                    justifyContent: "center",
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: columnsOpen ? (isDark ? "#56B7FD" : "#818CF8") : (isDark ? "#444" : "#E2E8F0"),
                  }}
                  onPress={() => {
                    if (settingsBtnRef.current) {
                      settingsBtnRef.current.measureInWindow(
                        (x: number, y: number, width: number, height: number) => {
                          setSettingsBtnPos({ x, y, width, height });
                          setColumnsOpen(!columnsOpen);
                        }
                      );
                    } else {
                      setColumnsOpen(!columnsOpen);
                    }
                  }}
                >
                  <Feather
                    name="settings"
                    size={14}
                    color={columnsOpen ? "#56B7FD" : (isDark ? "#aaa" : "#6B7280")}
                  />
                </TouchableOpacity>
                {columnsOpen && (
                  <RNModal
                    transparent={true}
                    visible={columnsOpen}
                    animationType="fade"
                    onRequestClose={() => setColumnsOpen(false)}
                  >
                    <Pressable
                      style={{
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.05)",
                      }}
                      onPress={() => setColumnsOpen(false)}
                    >
                      <Pressable
                        style={{
                          position: "absolute",
                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                          left: settingsBtnPos.x - 188, // shift left so it doesn't clip screen
                          backgroundColor: isDark ? colors.surface : "#FFFFFF",
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: isDark ? "#333" : "#E2E8F0",
                          paddingVertical: 10,
                          minWidth: 220,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.15,
                          shadowRadius: 20,
                          elevation: 10,
                        }}
                        onPress={(e) => e.stopPropagation()}
                      >
                        <Text
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: isDark ? "#aaa" : "#6B7280",
                            paddingHorizontal: 16,
                            paddingBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("showHideColumns")}
                        </Text>
                        <RNView style={{ height: 1, backgroundColor: isDark ? "#333" : "#F1F5F9", marginBottom: 4 }} />
                        <TouchableOpacity
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingVertical: 8,
                            paddingHorizontal: 16,
                          }}
                          onPress={() => {
                            if (hiddenCols.length === 0) {
                              dispatch(reportsActions.setUsageL36M2V2HiddenColumns(allColOptions.map((c) => c.value)));
                            } else {
                              dispatch(reportsActions.setUsageL36M2V2HiddenColumns([]));
                            }
                          }}
                        >
                          <RNView
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              borderWidth: 1.5,
                              borderColor: hiddenCols.length === 0 ? "#56B7FD" : (isDark ? "#555" : "#D1D5DB"),
                              backgroundColor: hiddenCols.length === 0 ? "#56B7FD" : "transparent",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: 10,
                            }}
                          >
                            {hiddenCols.length === 0 && (
                              <Feather name="check" size={12} color="#fff" />
                            )}
                          </RNView>
                          <Text
                            style={{
                              fontSize: 13,
                              color: colors.text,
                              fontWeight: "700",
                            }}
                          >
                            {t("selectAll")}
                          </Text>
                        </TouchableOpacity>
                        <RNView style={{ height: 1, backgroundColor: isDark ? "#333" : "#F1F5F9", marginBottom: 2 }} />
                        <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                          {getOrderedKeys(allColOptions.map(c => c.value)).map((colValue) => {
                            const col = allColOptions.find(c => c.value === colValue);
                            if (!col) return null;
                            const isVisible = !hiddenCols.includes(col.value);
                            const isDragging = dragActiveKey === col.value;
                            const isDragOver = dragOverKey === col.value;
                            return (
                              <RNView
                                key={col.value}
                                {...{ dataSet: { columnkey: col.value } } as any}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  opacity: isDragging ? 0.35 : 1,
                                  backgroundColor: isDragOver ? (isDark ? "#2a3b4c" : "#EFF6FF") : "transparent",
                                  borderTopWidth: isDragOver ? 2 : 0,
                                  borderTopColor: "#56B7FD",
                                }}
                              >
                                <Pressable
                                  onPressIn={() => startColumnDrag(col.value)}
                                  style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                >
                                  <Feather name="menu" size={12} color={isDark ? "#555" : "#CBD5E1"} />
                                </Pressable>
                                <TouchableOpacity
                                  style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                  onPress={() => toggleColumn(col.value)}
                                >
                                  <RNView
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor: isVisible ? "#56B7FD" : (isDark ? "#555" : "#D1D5DB"),
                                      backgroundColor: isVisible ? "#56B7FD" : "transparent",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {isVisible && (
                                      <Feather name="check" size={12} color="#fff" />
                                    )}
                                  </RNView>
                                  <Text style={{ fontSize: 13, color: isVisible ? colors.text : (isDark ? "#666" : "#9CA3AF"), fontWeight: isVisible ? "500" : "400" }}>
                                    {col.label}
                                  </Text>
                                </TouchableOpacity>
                              </RNView>
                            );
                          })}
                        </ScrollView>
                        {(columnOrder.length > 0 || hiddenCols.length > 0) && (
                          <>
                            <RNView style={{ height: 1, backgroundColor: isDark ? "#333" : "#F1F5F9", marginTop: 4 }} />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                              }}
                              onPress={resetColumnOrder}
                            >
                              <Feather name="rotate-ccw" size={13} color="#6B7280" />
                              <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                {t("resetLayout")}
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </Pressable>
                    </Pressable>
                  </RNModal>
                )}
              </RNView>
              <TouchableOpacity
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  backgroundColor: isDark ? "#333" : "#F3F4F6",
                  justifyContent: "center",
                  alignItems: "center",
                  borderWidth: 1,
                  borderColor: isDark ? "#444" : "#E2E8F0",
                  marginRight: 8,
                }}
                onPress={exportToExcel}
              >
                <Feather name="download" size={14} color={isDark ? "#aaa" : "#6B7280"} />
              </TouchableOpacity>
              <View style={styles.searchContainer}>
                <ScrollView ref={searchScrollRef} onContentSizeChange={() => searchScrollRef.current?.scrollToEnd({ animated: true })} horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }} contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}>
                  {searchTerms.map((term, i) => (
                    <View key={i} style={styles.searchChip}>
                      <Text style={styles.searchChipText}>{term}</Text>
                      <TouchableOpacity style={styles.searchChipRemove} onPress={() => setSearchTerms((prev) => prev.filter((_, j) => j !== i))}>
                        <Text style={styles.searchChipX}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                  <TextInput
                    style={styles.searchInputInner}
                    value={searchInput}
                    onChangeText={onSearchChange}
                    placeholder={searchTerms.length === 0 ? t("searchBar") : (t("addCriteria") || "Add criteria...")}
                    placeholderTextColor="#aaa"
                    onFocus={closeAllDropdowns}
                    onSubmitEditing={() => {
                      const trimmed = searchInput.trim();
                      if (trimmed) {
                        setSearchTerms((prev) => [...prev, trimmed]);
                        setSearchInput("");
                      }
                    }}
                    blurOnSubmit={false}
                  />
                </ScrollView>
              </View>
              <Text style={styles.pageSizeLabel}>{t("rowsPerPage")}</Text>
              {[50, 100, 200].map((n) => (
                <TouchableOpacity
                  key={n}
                  onPress={() => onPageSizeChange(n)}
                  style={[styles.pageSizeBtn, pageSize === n && styles.pageSizeBtnActive]}
                >
                  <Text style={[styles.pageSizeTxt, pageSize === n && styles.pageSizeTxtActive]}>
                    {n}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* ── Frozen column header ──────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.tableHeader, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "ItemName") return (
                  <View key={colKey} style={colItem as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("ItemName")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Item Name"])}{sortIndicator("ItemName")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "ItemName", colWidths.ItemName) } as any} />}
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Customer")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Customer"])}{sortIndicator("Customer")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Customer", colWidths.Customer) } as any} />}
                  </View>
                );
                if (colKey === "SlpName") return (
                  <View key={colKey} style={colSlp as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("SlpName")}>
                      <Text style={styles.headerCell}>{t(scoreCardTranslationMap["Sales Person"])}{sortIndicator("SlpName")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "SlpName", colWidths.SlpName) } as any} />}
                  </View>
                );
                if (colKey === "Year") return (
                  <View key={colKey} style={colYear as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Year")}>
                      <Text style={[styles.headerCell, styles.monthAlign]}>{t(scoreCardTranslationMap["Year"])}{sortIndicator("Year")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Year", colWidths.Year ?? COL_YEAR_W) } as any} />}
                  </View>
                );
                if (colKey === "Total") return (
                  <View key={colKey} style={[styles.colTotalBase, { width: colWidths.Total }, hiddenCols.includes("Total") && { display: "none" as const }]}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Total")}>
                      <Text style={[styles.headerCell, styles.monthAlign]}>{t(scoreCardTranslationMap["Total"])}{sortIndicator("Total")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Total", colWidths.Total) } as any} />}
                  </View>
                );
                return (
                  <View key={colKey} style={[styles.colMonthBase, { width: colWidths[colKey] }, hiddenCols.includes(colKey) && { display: "none" as const }]}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress(colKey)}>
                      <Text style={[styles.headerCell, styles.monthAlign]}>{periodLabel(colKey)}{sortIndicator(colKey)}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, colKey, colWidths[colKey]) } as any} />}
                  </View>
                );
              })}
            </Animated.View>
          </View>

          {/* ── Scrollable data rows ──────────────────────────────────────── */}
          <View style={{ flex: 1 }} onLayout={onDataAreaLayout}>
            {granularity !== "M" && periodLoading ? (
              <View style={{ height: dataAreaHeight, justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator size="large" color="#1976d2" />
                <Text style={styles.loadingText}>Loading {granularity === "D" ? "daily" : "weekly"} data...</Text>
              </View>
            ) : (
              <ScrollView
                horizontal
                onScroll={onBodyHScroll}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator={false}
              >
                <ScrollView
                  style={{ height: dataAreaHeight }}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator
                >
                  <View style={{ width: tableWidth }}>
                    {pageRows.map((row, idx) => {
                      const rowKey = (safePage - 1) * pageSize + idx;
                      const expanded = expandedRow === rowKey;
                      const rowSum = rowTotal(row);
                      return (
                        <View key={rowKey} style={[styles.tableRow, idx % 2 === 1 && styles.altRow]}>
                          {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                            if (colKey === "ItemName") return (
                              <CellWrapper key={colKey} style={[colItem as any, copiedKey === `${rowKey}-item` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-item`, row.ItemName)}>
                                <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                  {row.ItemName}
                                </Text>
                              </CellWrapper>
                            );
                            if (colKey === "Customer") return (
                              <CellWrapper key={colKey} style={[colCustomer as any, copiedKey === `${rowKey}-cust` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-cust`, row.Customer)}>
                                <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                  {row.Customer}
                                </Text>
                              </CellWrapper>
                            );
                            if (colKey === "SlpName") return (
                              <CellWrapper key={colKey} style={[colSlp as any, copiedKey === `${rowKey}-slp` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-slp`, row.SlpName)}>
                                <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                  {row.SlpName}
                                </Text>
                              </CellWrapper>
                            );
                            if (colKey === "Year") return (
                              <CellWrapper key={colKey} style={[colYear as any, copiedKey === `${rowKey}-year` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-year`, String(row.Year))}>
                                <Text selectable style={[styles.cell, styles.monthAlign]}>{row.Year}</Text>
                              </CellWrapper>
                            );
                            if (colKey === "Total") return (
                              <CellWrapper key={colKey} style={[styles.colTotalBase, { width: colWidths.Total }, copiedKey === `${rowKey}-tot` ? styles.copiedCell : undefined, hiddenCols.includes("Total") && { display: "none" as const }]} onLongPress={() => copyCell(`${rowKey}-tot`, String(rowSum))}>
                                <Text selectable style={[styles.cell, styles.rowTotalText]}>
                                  {rowSum.toLocaleString()}
                                </Text>
                              </CellWrapper>
                            );
                            const inR = granularity === "M" ? monthInRange(row.Year, MONTHS.indexOf(colKey), dateRange) : true;
                            const val = inR ? (((row as any)[colKey] as number) || 0) : 0;
                            const ck = `${rowKey}-${colKey}`;
                            return (
                              <CellWrapper key={colKey} style={[styles.colMonthBase, { width: colWidths[colKey] }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(colKey) && { display: "none" as const }]} onLongPress={() => copyCell(ck, inR ? String(val) : "–")}>
                                <Text selectable style={[styles.cell, styles.monthAlign]}>
                                  {inR ? val.toLocaleString() : "–"}
                                </Text>
                              </CellWrapper>
                            );
                          })}

                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </ScrollView>
            )}
          </View>

          {/* ── Frozen totals row ─────────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.totalsRow, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "ItemName") return (
                  <View key={colKey} style={colItem}>
                    <Text style={styles.totalCell}>{t("total")}</Text>
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer}>
                    <Text style={[styles.totalCell, styles.totalSubLabel]}>
                      {filtered.length.toLocaleString()} {t("filteredRows")}
                    </Text>
                  </View>
                );
                if (colKey === "SlpName") return (
                  <View key={colKey} style={colSlp} />
                );
                if (colKey === "Year") return (
                  <View key={colKey} style={colYear} />
                );
                if (colKey === "Total") return (
                  <CellWrapper
                    key={colKey}
                    style={[styles.colTotalBase, { width: colWidths.Total }, copiedKey === "ttl-Total" ? styles.copiedCell : undefined, hiddenCols.includes("Total") && { display: "none" as const }]}
                    onLongPress={() => copyCell("ttl-Total", String(colTotals["Total"] || 0))}
                  >
                    <Text selectable style={[styles.totalCell, styles.monthAlign]}>
                      {(colTotals["Total"] || 0).toLocaleString()}
                    </Text>
                  </CellWrapper>
                );
                const ck = `ttl-${colKey}`;
                return (
                  <CellWrapper
                    key={colKey}
                    style={[styles.colMonthBase, { width: colWidths[colKey] }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(colKey) && { display: "none" as const }]}
                    onLongPress={() => copyCell(ck, String(colTotals[colKey] || 0))}
                  >
                    <Text selectable style={[styles.totalCell, styles.monthAlign]}>
                      {(colTotals[colKey] || 0).toLocaleString()}
                    </Text>
                  </CellWrapper>
                );
              })}
            </Animated.View>
          </View>

          {/* Pagination */}
          <View style={styles.paginationRow}>
            <Text style={styles.paginationInfo}>
              {filtered.length === 0
                ? t("noResults")
                : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length.toLocaleString()}`}
            </Text>
            <View style={styles.paginationBtns}>
              <TouchableOpacity onPress={() => goToPage(1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>«</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(safePage - 1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.pageIndicator}>{t("page")} {safePage} / {totalPages}</Text>
              <TouchableOpacity onPress={() => goToPage(safePage + 1)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>›</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(totalPages)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>»</Text>
              </TouchableOpacity>
            </View>
          </View>

        </View>
      </View>
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" },
  loadingContainer: { flex: 1, justifyContent: "center", alignItems: "center" },
  loadingText: { marginTop: 12, fontSize: 14, color: isDark ? "#aaa" : "#555" },

  frozenHeader: { backgroundColor: isDark ? colors.background : "#eef2f6", padding: 16, paddingBottom: 0, zIndex: 1000 },
  pageTitle: { fontSize: 14, fontWeight: "600", color: colors.text, marginBottom: 2 },
  fetchedAt: { fontSize: 11, fontStyle: "italic", color: "#888", marginBottom: 6 },
  searchContainer: {
    flexDirection: "row", flexWrap: "nowrap", alignItems: "center",
    borderWidth: 1, borderColor: isDark ? "#444" : "#D1D5DB", borderRadius: 8,
    paddingHorizontal: 8, paddingVertical: 4,
    backgroundColor: isDark ? colors.surface : "#FFFFFF", minHeight: 36,
    width: 320, marginRight: 16,
  },
  searchChip: {
    flexDirection: "row", alignItems: "center",
    backgroundColor: isDark ? colors.primary + "33" : "#DBEAFE", borderRadius: 20,
    paddingLeft: 10, paddingRight: 6, paddingVertical: 4,
    marginRight: 4, marginVertical: 2,
  },
  searchChipText: { fontSize: 12, color: isDark ? "#93C5FD" : "#1D4ED8", fontWeight: "600" },
  searchChipRemove: {
    marginLeft: 6, width: 16, height: 16, borderRadius: 8,
    backgroundColor: isDark ? colors.primary + "66" : "#93C5FD", justifyContent: "center", alignItems: "center",
  },
  searchChipX: { fontSize: 9, color: isDark ? "#fff" : "#1E40AF", fontWeight: "700", lineHeight: 16 },
  searchInputInner: { flex: 1, minWidth: 120, fontSize: 13, height: 24, padding: 0, color: colors.text },
  filterPanel: {
    backgroundColor: isDark ? colors.surface : "#FFFFFF", borderRadius: 8, padding: 14, marginBottom: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: isDark ? 0.3 : 0.08, shadowRadius: 3, elevation: 2,
    zIndex: 1000,
    borderWidth: isDark ? 1 : 0, borderColor: isDark ? "#333" : "transparent"
  },
  filterHeaderRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  filterHeading: {
    fontSize: 12, fontWeight: "600", color: isDark ? "#aaa" : "#888",
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  resetBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4, backgroundColor: isDark ? "#333" : "#f0f0f0" },
  resetBtnText: { fontSize: 11, color: isDark ? "#ccc" : "#555", fontWeight: "600" as const },
  filterGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12, zIndex: 1000 },

  tableSection: { flex: 1, padding: 8, paddingTop: 0, paddingBottom: 8 },
  tableCard: {
    flex: 1, backgroundColor: isDark ? colors.surface : "#FFFFFF", borderRadius: 8, padding: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: isDark ? 0.3 : 0.08, shadowRadius: 3, elevation: 2,
    borderWidth: isDark ? 1 : 0, borderColor: isDark ? "#333" : "transparent"
  },

  tableTopBar: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 },
  tableTitle: { fontSize: 15, fontWeight: "600", color: colors.text },
  pageSizeRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  pageSizeLabel: { fontSize: 12, color: isDark ? "#aaa" : "#555" },
  pageSizeBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: isDark ? "#444" : "#ddd", backgroundColor: isDark ? colors.background : "#eef2f6" },
  pageSizeBtnActive: { backgroundColor: "#1976d2", borderColor: "#1976d2" },
  pageSizeTxt: { fontSize: 12, color: colors.text },
  pageSizeTxtActive: { color: "#fff", fontWeight: "600" },
  resetSortBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, backgroundColor: "#fff0f0", borderWidth: 1, borderColor: "#ffcdd2", marginRight: 8 },
  resetSortTxt: { fontSize: 12, color: "#d32f2f", fontWeight: "600" },
  granularityToggle: { flexDirection: "row", borderWidth: 1, borderColor: isDark ? "#444" : "#ddd", borderRadius: 6, overflow: "hidden", marginRight: 12 },
  granBtn: { paddingHorizontal: 10, paddingVertical: 6, backgroundColor: isDark ? colors.background : "#eef2f6" },
  granBtnActive: { backgroundColor: "#1976d2" },
  granTxt: { fontSize: 12, color: colors.text },
  granTxtActive: { color: "#fff", fontWeight: "600" },

  tableHeader: { flexDirection: "row", backgroundColor: isDark ? colors.surfaceVariant || "#2c2c2e" : "#f0f4f8", borderTopLeftRadius: 4, borderTopRightRadius: 4, borderBottomWidth: 2, borderBottomColor: isDark ? "#444" : "#dde3ea" },
  tableRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: isDark ? "#333" : "#f0f0f0" },
  altRow: { backgroundColor: isDark ? colors.surface : "#fafafa" },
  totalsRow: { flexDirection: "row", backgroundColor: isDark ? "#1A2E44" : "#e3f2fd", borderTopWidth: 2, borderTopColor: "#1976d2" },

  headerCell: { fontWeight: "700", color: isDark ? "#bbb" : "#444", fontSize: 11 },
  cell: { fontSize: 11, color: colors.text },
  expandableCell: { color: isDark ? "#90CAF9" : "#1976d2" },
  monthAlign: { textAlign: "right" },
  rowTotalText: { fontWeight: "600", color: isDark ? "#81C784" : "#1b5e20", textAlign: "right" },
  totalCell: { fontSize: 11, fontWeight: "700", color: isDark ? "#90CAF9" : "#0d47a1" },
  totalSubLabel: { fontSize: 10, fontWeight: "400", color: isDark ? "#aaa" : "#888" },

  copiedCell: { backgroundColor: isDark ? "#1b5e20" : "#c8e6c9" },

  colBase: { paddingHorizontal: 8, paddingVertical: 6, borderRightWidth: 1, borderRightColor: isDark ? "#333" : "#eee" },
  colYearBase: { paddingHorizontal: 4, paddingVertical: 6, borderRightWidth: 1, borderRightColor: isDark ? "#333" : "#eee" },
  colMonthBase: { paddingHorizontal: 4, paddingVertical: 6, borderRightWidth: 1, borderRightColor: isDark ? "#333" : "#eee" },
  colTotalBase: { paddingHorizontal: 4, paddingVertical: 6, borderRightWidth: 1, borderRightColor: isDark ? "#1976d2" : "#90caf9", backgroundColor: isDark ? "#1A2E44" : "#e8f0fe" },

  paginationRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 8 },
  paginationInfo: { fontSize: 12, color: isDark ? "#aaa" : "#666" },
  paginationBtns: { flexDirection: "row", alignItems: "center", gap: 4 },
  pageBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: isDark ? "#444" : "#ddd", backgroundColor: isDark ? colors.background : "#eef2f6" },
  pageBtnDisabled: { borderColor: isDark ? "#333" : "#eee", backgroundColor: isDark ? "#1A1A1A" : "#f5f5f5" },
  pageBtnTxt: { fontSize: 14, color: colors.text },
  pageIndicator: { fontSize: 12, color: isDark ? "#aaa" : "#555", marginHorizontal: 8 },
});
